import { useState } from 'react';
import { generateTxnLink } from '../../services/order';
import { showToast } from '../../utils/toast';

const extractLink = (r: any): string | undefined =>
  r?.redirectLink || r?.result?.data?.link || r?.data?.link || r?.link || r?.url;

export const useTxnLink = () => {
  const [isRegeneratingLink, setIsRegeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [linkExpiry, setLinkExpiry] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const resetLink = () => {
    setGeneratedLink(null);
    setLinkExpiry(null);
    setLinkCopied(false);
  };

  const generate = async (
    payload: Record<string, any>,
    partnerId?: string,
    onDone?: () => Promise<void> | void
  ) => {
    const missing = [
      ...(!partnerId ? ['partnerId'] : []),
      ...Object.entries(payload)
        .filter(([, v]) => v === undefined || v === null || v === '' || Number.isNaN(v))
        .map(([k]) => k),
    ];

    if (missing.length) {
      showToast.error(`Missing data: ${missing.join(', ')}`);
      return;
    }

    setIsRegeneratingLink(true);
    try {
      const result = await generateTxnLink(payload as any, String(partnerId));
      const link = extractLink(result);

      if (link) {
        setGeneratedLink(link);
        setLinkExpiry(result?.expiresIn ?? null);
        setLinkCopied(false);
        showToast.success('Transaction link generated');
      } else {
        showToast.error('Link generated but not found in the response');
      }
      await onDone?.();
    } catch (err) {
      showToast.error((err as Error).message);
    } finally {
      setIsRegeneratingLink(false);
    }
  };

  const copyLink = async () => {
    if (!generatedLink) return;
    try {
      await navigator.clipboard.writeText(generatedLink);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = generatedLink;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return {
    isRegeneratingLink, generatedLink, linkExpiry, linkCopied,
    setGeneratedLink, resetLink, generate, copyLink,
  };
};